import { _ } from "../../core/polyglot/polyglot.js";
import {
	formatAsMonth,
	formatAsMonthAndYear,
} from "../../core/helpers/date-formatting-util.js";
import { getCSSVariable } from "../../core/helpers/css-util.js";
import chartBuilder from "../../core/charts.js";
import chartDisplaySettings from "../../core/charts/chartDisplaySettings.js";
import responsive from "../../core/responsive.js";

/**
 * Chart comparing two business data: profit, accumulated profit,
 * income, accumulated income. The two business data can come from two
 * different companies or two periods of the same one.
 *
 * @param {string[]} visibleSeriesIds - Series that should be
 *   selected in the chart
 */
export default class BusinessTrendChart {
	constructor({
		currentPeriod,
		onXAxisUpdated = () => {},
		onPlotbandClicked = () => {},
		onPlotbandHovered = () => {},
		onPlotbandExited = () => {},
		visibleSeriesIds,
	}) {
		this._chartXAxisId = "business-overview-xaxis";

		this._currentPeriod = currentPeriod;

		this._onXAxisUpdated = onXAxisUpdated;
		this._onPlotbandClicked = onPlotbandClicked;
		this._onPlotbandHovered = onPlotbandHovered;
		this._onPlotbandExited = onPlotbandExited;

		this._visibleSeriesIds = visibleSeriesIds;

		this._createChart();
	}

	getChartOptions() {
		return this._chartOptions;
	}

	setCurrentPeriod(currentPeriod) {
		this._currentPeriod = currentPeriod;
		this._updateXAxis();
	}

	/**
	 * Convert an array of numbers, one for each month, into an array
	 * of 2D points in the chart. Each number of the array is used as
	 * the Y coordinate.
	 */
	monthValuesTo2DCoordinate(monthValues, periods) {
		return monthValues.map((monthValue, month) =>
			this._monthValueTo2DCoordinate(monthValue, periods, month),
		);
	}

	/**
	 * Convert a numbers into a 2D point in the chart. The number is
	 * used as the Y coordinate.
	 */
	_monthValueTo2DCoordinate(monthValue, periods, month) {
		const initialMonth = periods[0];
		const initialYear = initialMonth.getYear();

		const initialMonthIndex = initialMonth.getStart().getMonth();
		const initialYearIndex = initialYear.getStart().getFullYear();

		const that = this;

		return {
			period: periods[month],
			x: new Date(
				initialYearIndex,
				initialMonthIndex + month,
				1,
			).valueOf(),
			y: monthValue,
			events: {
				mouseOver: function () {
					that._plotbandHovered(that._plotbandAtPoint(this));
				},
				mouseOut: function () {
					that._plotbandExited(that._plotbandAtPoint(this));
				},
			},
		};
	}

	_colors() {
		const incomeColor = {
			current: getCSSVariable("--kpi-income-color"),
			compared: getCSSVariable("--compared-income-color"),
		};

		const accumulatedIncomeColor = {
			current: getCSSVariable("--kpi-accumulated-income-color"),
			compared: getCSSVariable("--compared-accumulated-income-color"),
		};

		const profitColor = {
			current: getCSSVariable("--kpi-profit-color"),
			compared: getCSSVariable("--compared-profit-color"),
		};

		const accumulatedProfitColor = {
			current: getCSSVariable("--kpi-accumulated-profit-color"),
			compared: getCSSVariable("--compared-accumulated-profit-color"),
		};

		return {
			income: incomeColor,
			"accumulated-income": accumulatedIncomeColor,
			profit: profitColor,
			"accumulated-profit": accumulatedProfitColor,
		};
	}

	_pointMarkers() {
		const accumulatedProfitPointMarker = {
			current: this._definePointMarker({
				isCompared: false,
				color: this._colors()["accumulated-profit"],
			}),
			compared: this._definePointMarker({
				isCompared: true,
				color: this._colors()["accumulated-profit"],
			}),
		};

		const accumulatedIncomePointMarker = {
			current: this._definePointMarker({
				isCompared: false,
				color: this._colors()["accumulated-income"],
			}),
			compared: this._definePointMarker({
				isCompared: true,
				color: this._colors()["accumulated-income"],
			}),
		};

		return {
			"accumulated-profit": accumulatedProfitPointMarker,
			"accumulated-income": accumulatedIncomePointMarker,
		};
	}

	_definePointMarker({ isCompared = false, color }) {
		const lineColor = this._seriesColor({ isCompared, color });

		return {
			dataLabels: {
				enabled: false,
			},
			marker: {
				symbol: "circle",
				lineWidth: isCompared ? 1 : 3,
				lineColor,
				fillColor: isCompared ? lineColor : "#ffffff",
				radius: 6,
			},
		};
	}

	_createXAxisOptions() {
		const that = this;

		return chartBuilder
			.xDateAxis()
			.set("id", this._chartXAxisId)
			.set("labels", {
				style: { fontSize: "12px" },
				rotation: responsive.isMobileBreakpoint() ? -90 : 0,
				formatter: function () {
					return that._xAxisLabelFormatter(this);
				},
			})
			.setMonthTickInterval()
			.setPeriods({
				periods: this._currentPeriod.getYear().asMonths(),
				selectedPeriod: this._currentPeriod,
			})
			.setSelectablePeriods({
				plotBandClicked: this._onPlotbandClicked,
			})
			.setPlotBandEvents({
				mouseover: function () {
					that._plotbandHovered(this);
				},
				mouseout: function () {
					that._plotbandExited(this);
				},
			});
	}

	_plotbandHovered(plotband) {
		plotband.svgElem.attr("fill", chartDisplaySettings.hoverColor);

		// in-between plotbands
		if (plotband.options.index === undefined) {
			return;
		}

		this._onPlotbandHovered(plotband);
	}

	_plotbandExited(plotband) {
		plotband.svgElem.attr("fill", plotband.options.color);

		// in-between plotbands
		if (plotband.options.index === undefined) {
			return;
		}

		this._onPlotbandExited(plotband);
	}

	_updateXAxis() {
		this._chartOptions.xAxis = this._createXAxisOptions().build();
		this._onXAxisUpdated(this._chartXAxisId, this._chartOptions.xAxis);
	}

	_createChart() {
		this._chartOptions = chartBuilder()
			.set("plotOptions", {
				series: chartBuilder
					.seriesPlotOptions()
					.setSelectablePoints({
						onClick: (point) => {
							this._onPlotbandClicked(
								this._plotbandAtPoint(point),
							);
						},
					})
					.build(),
			})
			.set("xAxis", this._createXAxisOptions())
			.set("yAxis", chartBuilder.yAxis())
			.set({
				tooltip: {
					formatter: () => false,
				},
			})
			.set("series", [
				this._createProfitSeries(),
				this._createAccumulatedProfitSeries(),
				this._createIncomeSeries(),
				this._createAccumulatedIncomeSeries(),
				this._createProfitSeries({ isCompared: true }),
				this._createAccumulatedProfitSeries({ isCompared: true }),
				this._createIncomeSeries({ isCompared: true }),
				this._createAccumulatedIncomeSeries({ isCompared: true }),
			])
			.build();
	}

	_plotbandAtPoint(point) {
		const periodIndex = point.period.getPeriodIndex();

		return point.series.xAxis.plotLinesAndBands[periodIndex - 1];
	}

	_xAxisLabelFormatter(label) {
		const currentDate = new Date(label.value);

		if (label.isFirst || currentDate.getMonth() === 0) {
			return formatAsMonthAndYear(currentDate);
		}
		return formatAsMonth(currentDate);
	}

	_seriesZIndex({ isCompared, id }) {
		// Rationale for z-index:
		// - lines should be above rectangles
		// - current version above compared version
		switch (id) {
			case "profit":
			case "income": {
				return isCompared ? 1 : 2;
			}
			case "accumulated-income":
			case "accumulated-profit": {
				return isCompared ? 3 : 4;
			}
			default: {
				return 0;
			}
		}
	}

	_seriesId({ isCompared, id }) {
		return `${id}${isCompared ? "_compared" : ""}`;
	}

	_seriesName({ isCompared, name }) {
		if (isCompared) {
			return [name, "(", _("Compared"), ")"];
		}
		return name;
	}

	_seriesColor({ isCompared, color }) {
		return isCompared ? color.compared : color.current;
	}

	_createProfitSeries({ isCompared = false } = {}) {
		return this._createBoxSeries({
			isCompared,
			id: "profit",
			name: _("Profit"),
			defaultVisibility: true,
		});
	}

	_createIncomeSeries({ isCompared = false } = {}) {
		return this._createBoxSeries({
			isCompared,
			id: "income",
			name: _("Income"),
			defaultVisibility: false,
		});
	}

	_createBoxSeries({ isCompared, id, name, defaultVisibility }) {
		const color = this._colors()[id];

		return chartBuilder
			.series()
			.set({
				zIndex: this._seriesZIndex({ isCompared, id }),
				id: this._seriesId({ isCompared, id }),
				name: this._seriesName({ isCompared, name }),
				type: "column",
				color: this._seriesColor({ isCompared, color }),
				dataLabels: {
					enabled: false,
				},
			})
			.set("visible", this._isVisible({ id, defaultVisibility }))
			.build();
	}

	_createAccumulatedProfitSeries({ isCompared = false } = {}) {
		return this._createScatterSeries({
			isCompared,
			id: "accumulated-profit",
			name: _("Accumulated profit"),
			defaultVisibility: true,
		});
	}

	_createAccumulatedIncomeSeries({ isCompared = false } = {}) {
		return this._createScatterSeries({
			isCompared,
			id: "accumulated-income",
			name: _("Accumulated income"),
			defaultVisibility: false,
		});
	}

	_createScatterSeries({ isCompared, id, name, defaultVisibility }) {
		const color = this._colors()[id];
		const pointMarker = this._pointMarkers()[id];

		return chartBuilder
			.scatterSeries()
			.set({
				zIndex: this._seriesZIndex({ isCompared, id }),
				id: this._seriesId({ isCompared, id }),
				name: this._seriesName({ isCompared, name }),
				lineColor: this._seriesColor({ isCompared, color }),
			})
			.set(isCompared ? pointMarker.compared : pointMarker.current)
			.set("visible", this._isVisible({ id, defaultVisibility }))
			.build();
	}

	_isVisible({ id, defaultVisibility }) {
		if (!this._visibleSeriesIds) return defaultVisibility;
		return this._visibleSeriesIds.includes(id);
	}
}
